import React, { useEffect, useState } from 'react';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import cx from 'classnames';

import { ReactComponent as DropdownIcon } from 'assets/icons/arrow-down.svg';
import { ReactComponent as CheckCircleIcon } from 'assets/icons/check_circle.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import { Asterix } from 'components/Form/HelperText/HelperText';
import FooterAwarePopper from 'components/FooterAwarePopper/FooterAwarePopper';
import insuranceStyles from 'components/BookingForm/Step3/InsuranceSection/InsuranceSection.module.scss';
import { Insurance } from 'interfaces/insuranceType';
import {
  INSURANCE_PAYMENT_METHOD_OPTIONS,
  ModeOption,
} from 'pages/ClinicDetailsPage/BookNowPanel/utils/constants';
import { useAppSelector } from 'redux/reduxHooks';
import { getInsurances } from 'services/APIs';
import { formatFullPhoneNumber } from 'utils/formatPhoneNumber';

import styles from '../ContactDetails.module.scss';

interface ContactInsuranceProps {
  insurance: Insurance | null;
  isPayForMyself: boolean;
  insurancePaymentMethod: ModeOption | null;
  onChangeInsurance: (insurance: Insurance | null) => void;
  onTogglePayForMyself: () => void;
  onChangeInsurancePaymentMethod: (paymentMethod: ModeOption | null) => void;
}

const ContactInsurance = ({
  insurance,
  isPayForMyself,
  insurancePaymentMethod,
  onChangeInsurance,
  onTogglePayForMyself,
  onChangeInsurancePaymentMethod,
}: ContactInsuranceProps) => {
  const { clinicData } = useAppSelector((state) => state.clinicDetailsSlice);

  const [insuranceOptions, setInsuranceOptions] = useState<Insurance[]>([]);

  useEffect(() => {
    const loadInsurances = async () => {
      const result = await getInsurances(clinicData.id);

      setInsuranceOptions(result.data || []);
    };

    loadInsurances();
  }, [clinicData.id]);

  const renderInsuranceMessage = (selectedInsurance: Insurance) => {
    if (selectedInsurance.isActive) {
      return (
        <div className={insuranceStyles['info']}>
          Insurance accepted by {clinicData.name}
        </div>
      );
    }

    return (
      <div className={insuranceStyles['inactive-warning']}>
        <span>
          {clinicData.name} does not allow this insurance provider to schedule
          online.
        </span>
        <span>
          Please call us at{' '}
          <a href={`tel:+${clinicData.phone}`}>
            {formatFullPhoneNumber(clinicData.phone)}
          </a>{' '}
          to schedule your appointment.
        </span>
      </div>
    );
  };

  return (
    <div className={styles['insurance-section']}>
      <h3>
        Insurance provider <Asterix error="This field is required" />
      </h3>

      <Autocomplete
        options={insuranceOptions}
        value={insurance}
        getOptionLabel={(option) => option.name}
        onChange={(_, value) => onChangeInsurance(value)}
        disabled={isPayForMyself}
        PopperComponent={FooterAwarePopper}
        classes={{
          option: insuranceStyles['popper-insurance-item'],
          noOptions: insuranceStyles['popper-insurance-item'],
        }}
        renderInput={(params) => (
          <div
            className={insuranceStyles['input-wrapper']}
            ref={params.InputProps.ref}
          >
            {insurance ? (
              <>
                <button
                  type="button"
                  className={insuranceStyles['selected-insurance']}
                  onClick={() => onChangeInsurance(null)}
                >
                  <div>
                    {insurance.isActive ? (
                      <CheckCircleIcon
                        className={insuranceStyles['check-circle']}
                      />
                    ) : (
                      <WarningIcon className={insuranceStyles['warning']} />
                    )}
                    <span>{insurance.name}</span>
                  </div>
                </button>
                {renderInsuranceMessage(insurance)}
              </>
            ) : (
              <input
                {...params.inputProps}
                placeholder="Select Insurance Provider"
              />
            )}
          </div>
        )}
      />

      <div className={insuranceStyles['seperator']} />

      <div className={insuranceStyles['checkbox-section']}>
        <FormControlLabel
          checked={isPayForMyself}
          onChange={onTogglePayForMyself}
          classes={{ label: insuranceStyles['label'] }}
          control={<Checkbox classes={{ input: insuranceStyles['input'] }} />}
          label="I'm paying for myself"
        />
      </div>

      {isPayForMyself && (
        <div
          className={cx(
            insuranceStyles['insurance-payment-method-section'],
            styles['payment-method-section']
          )}
        >
          <Autocomplete
            options={INSURANCE_PAYMENT_METHOD_OPTIONS}
            value={insurancePaymentMethod}
            getOptionLabel={(option) => option.label}
            PopperComponent={FooterAwarePopper}
            classes={{
              option: insuranceStyles['popper-insurance-item'],
              noOptions: insuranceStyles['popper-insurance-item'],
            }}
            popupIcon={<DropdownIcon />}
            closeIcon={<></>}
            onChange={(_, value) => onChangeInsurancePaymentMethod(value)}
            renderInput={(params) => (
              <div
                {...params.InputProps}
                className={insuranceStyles['input-wrapper']}
              >
                {insurancePaymentMethod ? (
                  <button
                    type="button"
                    className={insuranceStyles['selected-insurance']}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChangeInsurancePaymentMethod(null);
                    }}
                  >
                    <div>
                      <span>{insurancePaymentMethod.label}</span>
                    </div>
                  </button>
                ) : (
                  <input
                    {...params.inputProps}
                    placeholder="Select payment option"
                    readOnly
                  />
                )}
                <div
                  className={
                    insuranceStyles['insurance-payment-method-section-arrow']
                  }
                >
                  {params.InputProps.endAdornment}
                </div>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default ContactInsurance;
